/**
 * API Configuration Module
 * 
 * Centralized configuration for all API endpoints and service keys.
 * All API endpoints are defined here in one place for easy management.
 * 
 * @module apiConfig
 */

import { pathWithAppBase } from '@/lib/appBasePath';

/**
 * Environment type for the application
 */
export type Environment = 'local' | 'staging' | 'production';

/** Legacy monolith (`/api/*` — audience verification, legacy email, etc.). Never default to the Next.js dev server. */
const DEFAULT_MAIN_API_ORIGIN = 'https://staging-api.boostt.ai';
/** App origin for redirects when `NEXT_PUBLIC_SELF_URL` is unset at build time. */
const DEFAULT_SELF_ORIGIN = 'https://staging-v1-app.boostt.ai';

function normalizeApiOrigin(raw: string): string {
  return raw.trim().replace(/\/+$/, '');
}

/**
 * Resolves `NEXT_PUBLIC_API_URL` with a real fallback so Axios `baseURL` is never the string `"undefined/api/"`,
 * which browsers resolve against the current page (e.g. `/v2/email/undefined/api/...`).
 */
export function getMainApiOrigin(): string {
  const v = process.env.NEXT_PUBLIC_API_URL;
  return normalizeApiOrigin((v && v.trim()) || DEFAULT_MAIN_API_ORIGIN);
}

/** Origin only (no path) for `location.href` login / account-disabled redirects. */
export function getSelfOriginForRedirects(): string {
  const v = process.env.NEXT_PUBLIC_SELF_URL;
  return normalizeApiOrigin((v && v.trim()) || DEFAULT_SELF_ORIGIN);
}

/**
 * Centralized API Configuration
 * All base URLs and service keys
 */
const ANALYTICS_API_BASE = 'https://analytics.staging-api.boostt.ai';
const LEGACY_LAMBDA_ANON_VISITOR = 'https://ksbjsdamdhvt7hkioqpbro4ngy0dlqqx.lambda-url.us-east-2.on.aws';

function getAnalyticsBaseUrl() {
  const env = process.env.NEXT_PUBLIC_ANALYTICS_API_URL;
  if (!env) return ANALYTICS_API_BASE;
  const trimmed = env.replace(/\/+$/, '');
  if (trimmed === LEGACY_LAMBDA_ANON_VISITOR) return ANALYTICS_API_BASE;
  return trimmed;
}

export const apiConfig = {
  environment: (process.env.NEXT_PUBLIC_ENV_NAME as Environment) || 'local',

  // Base URLs
  baseUrls: {
    main: getMainApiOrigin(),
    analytics: getAnalyticsBaseUrl(),
    digitalAd: process.env.NEXT_PUBLIC_DIGITAL_AD_API_URL || 'https://digital-ad.staging-api.boostt.ai',
    integrationService:
      process.env.NEXT_PUBLIC_INTEGRATION_SERVICE_API_URL || ANALYTICS_API_BASE,
    self: getSelfOriginForRedirects(),
    wordpress: process.env.NEXT_PUBLIC_WORDPRESS_URL || '',
    subapp: process.env.NEXT_PUBLIC_SUBAPP_URL || '',
    agentic: (
      process.env.NEXT_PUBLIC_AGENTIC_URL ||
      'https://d1e1eno4w2tqv2.cloudfront.net/prod'
    ).replace(/\/+$/, ''),
    // New Services
    userLogin: process.env.NEXT_PUBLIC_USER_LOGIN_API_URL || 'https://user-login.staging-api.boostt.ai',
    rbac: process.env.NEXT_PUBLIC_RBAC_API_URL || 'https://rbac.staging-api.boostt.ai',
    pricing: process.env.NEXT_PUBLIC_PRICING_API_URL || 'https://pricing.staging-api.boostt.ai',
    payment: process.env.NEXT_PUBLIC_PAYMENT_API_URL || 'https://payment.staging-api.boostt.ai',
    invoice: process.env.NEXT_PUBLIC_INVOICE_API_URL || 'https://invoice-service.boostt.ai',
    prediction: process.env.NEXT_PUBLIC_PREDICTION_API_URL || 'https://prediction.staging-api.boostt.ai',
    email: process.env.NEXT_PUBLIC_EMAIL_API_URL || 'https://email.staging-api.boostt.ai',
    directMail: process.env.NEXT_PUBLIC_DM_API_URL || 'https://dm.staging-api.boostt.ai',
    journey: process.env.NEXT_PUBLIC_JOURNEY_API_URL || 'https://journey.staging-api.boostt.ai',
    brands: process.env.NEXT_PUBLIC_BRANDS_API_URL || 'https://brand.staging-api.boostt.ai',
    /** Identity/visitor service (anon-visitor, visitor counts, WS). Replaces legacy Lambda. */
    identity: process.env.NEXT_PUBLIC_IDENTITY_SERVICE_URL || 'https://visitor.staging-api.boostt.ai',
    qrCode: process.env.NEXT_PUBLIC_QR_CODE_API_URL || 'https://qr-service.boostt.ai',
    audience:
      process.env.NEXT_PUBLIC_AUDIENCE_BUILDER_API_URL ||
      process.env.NEXT_PUBLIC_AUDIENCE_SERVICE_URL ||
      'https://audience.staging-api.boostt.ai',
    designService: process.env.NEXT_PUBLIC_DESIGN_SERVICE_API_URL || 'https://design-service.boostt.ai',
    // Voice API
    voice: process.env.NEXT_PUBLIC_VOICE_API_URL || 'https://voice.staging-api.boostt.ai',
    // Notification API
    notification: process.env.NEXT_PUBLIC_NOTIFICATION_API_URL || 'https://notification.staging-api.boostt.ai',
    // Command Center data layer (staging Lambda Function URL)
    commandCenter:
      process.env.NEXT_PUBLIC_COMMAND_CENTER_API_URL ||
      'https://an5battz4rzkcfrpqbjcvegjrm0afwyi.lambda-url.us-east-2.on.aws',
    // pipeline-svc campaign dashboard (per-campaign metric values by channel)
    pipelineDashboard:
      process.env.NEXT_PUBLIC_PIPELINE_DASHBOARD_API_URL ||
      'https://u6ivm6k3celqih25wwsihyje2i0hyjkj.lambda-url.us-east-2.on.aws',
    /** WebSocket push for the notifications drawer (see useNotificationSocket.ts). Falls back to REST polling if unset/unreachable. */
    notificationWs: process.env.NEXT_PUBLIC_NOTIFICATION_WS_URL || 'wss://notification-ws.staging-api.boostt.ai',
  },

  // API Endpoints - Main API
  endpoints: {
    // Auth & User
    logout: '/api/logout',
    userLogin: '/api/userLogin',
    uploadImage: '/uploadImage',
    impersonateStop: '/api/impersonate-stop',

    // Brands
    brandsGetAll: '/v1/brands/getAll',
    brandsGet: '/v1/brands/get',
    brandsEdit: 'v1/brands/edit',
    brandsCreate: '/v1/brands/create',
    brandsDelete: '/v1/brands/delete',

    // Payment & Banking
    achCheckBalance: '/user/payment/ach/check-balance',
    achVerifyMicrodeposits: '/user/payment/ach/verify-microdeposits',
    achAdd: '/user/payment/ach/add',
    invoice: '/invoice',
    walletRecharge: 'wallet/recharge',

    // Orders
    orderEmailInitiate: 'order/email/initiate',
    dmInitiateDataOrder: 'dm/initiateDataOrder',
    /** @deprecated Prefer Audience Builder `POST purchases/list/user_audiences` */
    dmGetUserSearchHistory: 'dm/getUserSearchHistory',

    // Email (list accounts: use GET /api/v1/email-accounts/ on email service)
    emailMailboxGetAll: '/email/mailbox/getAll', // deprecated - use email service list
    emailMailboxCreate: '/api/v1/email-accounts/',
    emailMailboxDelete: '/email/mailbox/delete',
    emailMailboxTags: 'v1/email/mailbox/tags',
    emailMailboxMarkAsRead: 'v1/email/mailbox/mark-as-read',
    emailMailbox: 'v1/email/mailbox',
    emailMailboxGetByTags: 'v1/email/mailbox/getByTags',
    emailMailboxTagsAssign: 'v1/email/mailbox/tags/assign',
    emailMailboxReply: 'v1/email/mailbox/reply',
    emailTemplateGet: '/email/template/get',
    emailTemplateDelete: '/email/template/delete',
    emailTemplateCreate: '/email/template/create',
    emailTemplateEdit: '/email/template/edit',
    emailCampaignGet: '/email/campaign/get',
    emailCampaignGetCampaigns: 'email/campaign/getCampaigns',
    emailCampaignEdit: '/email/campaign/edit',
    emailCampaignDelete: 'email/campaign/delete',
    emailCampaignAnalytics: '/email/campaign/analytics',
    emailCampaignAnalyticsExport: '/email/campaign/analytics/export',

    // Journey
    journeyCampaignGet: 'journey/campaign/get',
    journeyCampaignAnalytics: 'journey/campaign/analytics',
    journeyAnalytics: 'journey/analytics',
    journeyAnalyticsSummary: 'journey/analytics/summary',
    journeyDelete: 'journey/delete',
    journeyDuplicate: 'journey/duplicate',
    journeyActivate: 'journey/activate',
    journeyPause: 'journey/pause',
    journeyEdit: 'journey/edit',
    journeyList: 'journey/list',

    // CDM & Predictions
    cdmModelList: '/cdm/model/list',
    cdmModelGet: 'cdm/model/get',
    cdmModelReport: '/cdm/model/report',
    cdmModelAnalysis: 'cdm/model/analysis',
    cdmModelCreate: 'cdm/model/create',
    cdmModelDelete: 'cdm/model/delete',
    cdmModelDownloadModelData: '/cdm/model/downloadModelData',
    cdmEnrichmentGetPricing: 'cdm/enrichment/getPricing',

    // Visualization
    visualizationSampleGetFieldsAndDataType: '/visualization/sample/getFieldsAndDataType',
    visualizationSampleGetFieldsData: '/visualization/sample/getFieldsData',
    visualizationStoreGet: 'visualization/store/get',

    // Address Curation
    addressCurationCreate: '/address-curation/create',
    addressCurationEdit: '/address-curation/edit',
    addressCurationGet: '/address-curation/get',
    addressCurationUpdate: '/address-curation/update',
    addressCurationDelete: '/address-curation/delete',
    addressCurationDemographics: '/address/curation/demographics',

    // Shop Locator & Geocoding
    shopLocatorSearch: '/shop-locator/search',
    geocodingReverse: '/geocoding/reverse',

    // USPS Tracking
    informVisibilityScansGet: '/informVisibility/scans/get',

    // Export
    exportData: '/export/data',

    // Google Auth
    authGoogle: '/auth/google',

    // Audience
    audienceGet: '/api/v1/audience',
    audienceCreate: 'audience/create',
    audienceEdit: 'audience/edit',
    audienceDelete: 'audience/delete',
    audienceList: 'audience/list',

    // Coupons
    couponsRedemptionLink: 'coupons/redemption/link',

    // Logic
    logicAnalyticsExport: 'logic/analytics/export',
    logicGet: 'logic/get',
    logicCreate: 'logic/create',
    logicEdit: 'logic/edit',
    logicDelete: 'logic/delete',

    // Direct Mail
    dmCampaignGet: 'dm/campaign/get',
    dmCampaignCreate: 'dm/campaign/create',
    dmCampaignEdit: 'dm/campaign/edit',
    dmCampaignDelete: 'dm/campaign/delete',
    dmCampaignList: 'dm/campaign/list',

    // EDDM
    eddmCampaignGet: 'eddm/campaign/get',
    eddmCampaignCreate: 'eddm/campaign/create',
    eddmCampaignEdit: 'eddm/campaign/edit',
    eddmCampaignDelete: 'eddm/campaign/delete',
    eddmCampaignList: 'eddm/campaign/list',

    // External
    generateWebhook: '/external',

    // Invoice Download
    invoiceDownload: '/api/invoice/download',
  },

  // Voice API Endpoints (Scan-to-Voice / voice.staging-api.boostt.ai OpenAPI)
  voiceEndpoints: {
    workspaces: '/api/workspaces',
    campaigns: '/api/campaigns',
    campaignTemplates: '/api/campaign-templates',
    smsTemplates: '/api/sms-templates',
    leads: '/api/leads',
    dripSequences: '/api/drip-sequences',
    outbound: '/api/outbound',
    agents: '/api/agents',
    voices: '/api/agents/voices',
    workflows: '/api/workflows',
    workflowTemplates: '/api/workflow-templates',
    agentTemplates: '/api/agent-templates',
    voice: '/api/voice',
    knowledge: '/api/knowledge',
    phoneNumbers: '/api/phone-numbers',
    pool: '/api/pool',
    callbacks: '/api/callbacks',
    integrations: '/api/integrations',
    billing: '/api/billing',
    analytics: '/api/analytics',
    agentBuilder: '/api/agent-builder',
    qr: '/api/qr',
    tokens: '/api/tokens',
    contacts: '/api/contacts',
    events: '/api/events',
    segments: '/api/segments',
    offers: '/api/offers',
    seasonalTriggers: '/api/seasonal-triggers',
    voicemails: '/api/voicemails',
    /** F10 — confirmed live 2026-08-21. */
    intents: '/api/intents',
  },

  // Notification API Endpoints (notification.staging-api.boostt.ai OpenAPI)
  notificationEndpoints: {
    list: '/api/v1/notifications',
    markRead: (id: string) => `/api/v1/notifications/${id}/read`,
  },

  // Integration Service Endpoints
  integrationEndpoints: {
    integrations: 'integrations',
  },

  // Analytics API Endpoints
  analyticsEndpoints: {
    anonVisitor: '/api/anon-visitor',
    posthogEvents: '/api/posthog/events',
  },

  // User Login API Endpoints
  userLoginEndpoints: {
    // Authentication
    login: '/api/userLogin',
    logout: '/api/logout',
    validateSession: '/api/validateSession',
    // Registration
    register: '/api/newRegistration',
    activate: '/api/activate',
    resendActivation: '/api/resendactivationlink',
    // User Profile
    getUserDetails: '/api/getUserDetails',
    updateUserDetails: '/api/updateUserDetails',
    getStorageSpace: '/api/getUserStorageSpace',
    updateJourney: '/api/updateJourney',
    // GET reads / PUT saves the post-login platform onboarding "don't show again" flag
    onboardingPreference: '/api/onboardingPreference',
    // Password Management
    updatePassword: '/api/updatePassword',
    forgotPassword: '/api/forgotPassword',
    validateToken: '/api/password',
    resetPassword: '/api/password/change',
    // Address Management
    getAddresses: '/api/getAddressList',
    addAddress: '/api/addNewAddress',
    editAddress: '/api/editAddress',
    deleteAddress: '/api/deleteAddress',
    // Utility
    isUserAnonymous: '/api/isUserAnonymous',
    getCart: '/api/getCart',
    // Administration
    adminCheck: '/api/admin/check',
    adminList: '/api/admin/list',
    adminAdd: '/api/admin/add',
    adminRemove: '/api/admin/remove',
    listUsers: '/api/administration/listUsers',
    exportUsers: '/api/administration/exportUsers',
    activateUser: '/api/administration/user/activate',
    deactivateUser: '/api/administration/user/deactivate',
    // No session cookie — reached from an admin-notification email link,
    // token in the URL proves the caller followed a real link. See
    // user-login's app/services/admin_review_token.py.
    reviewUser: '/api/administration/user/review',
    reviewUserAction: '/api/administration/user/review/action',
    impersonationSession: '/api/impersonation/session',
    impersonationListUsers: '/api/impersonation/listUsers',
    impersonationStart: '/api/impersonation/start',
    impersonationStop: '/api/impersonation/stop',
    // Release Notes
    releaseNotes: '/api/admin/release-notes',
    publishedReleaseNotes: '/api/release-notes',
    // Feedback
    submitFeedback: '/api/feedback',
    // Analytics
    userLoginAnalytics: '/analytics/user-logins',
  },

  // RBAC API Endpoints
  rbacEndpoints: {
    // Organizations
    createOrg: '/api/orgs',
    getMyOrgs: '/api/orgs/my',
    getOrg: '/api/orgs', // + /{org_id}
    updateOrg: '/api/orgs', // + /{org_id}
    deleteOrg: '/api/orgs', // + /{org_id}
    transferOwnership: '/api/orgs', // + /{org_id}/transfer
    getOrgMembers: '/api/orgs', // + /{org_id}/members
    inviteMember: '/api/orgs', // + /{org_id}/members
    acceptInvite: '/api/orgs/invites/accept',
    // Accounts
    createAccount: '/api/accounts',
    listAccounts: '/api/accounts',
    getAccount: '/api/accounts', // + /{account_id}
    updateAccount: '/api/accounts', // + /{account_id}
    deleteAccount: '/api/accounts', // + /{account_id}
    grantAccountAccess: '/api/accounts', // + /{account_id}/access
    getAccountTree: '/api/accounts/tree', // + ?org_id={org_id}
    // Sub-Accounts
    createSubAccount: '/api/accounts', // + /{account_id}/sub-accounts
    listSubAccounts: '/api/sub-accounts',
    getSubAccount: '/api/sub-accounts', // + /{sub_account_id}
    updateSubAccount: '/api/sub-accounts', // + /{sub_account_id}
    deleteSubAccount: '/api/sub-accounts', // + /{sub_account_id}
    // Roles
    getSystemRoles: '/api/roles/system',
    listRoles: '/api/roles',
    createRole: '/api/roles',
    getRole: '/api/roles', // + /{role_id}
    updateRole: '/api/roles', // + /{role_id}
    deleteRole: '/api/roles', // + /{role_id}
    setRolePermissions: '/api/roles', // + /{role_id}/permissions
    addRolePermission: '/api/roles', // + /{role_id}/permissions
    setRoleConstraints: '/api/roles', // + /{role_id}/constraints
    // Permissions
    listPermissions: '/api/permissions',
    getPermissionsGrouped: '/api/permissions/grouped',
    getPermissionCategories: '/api/permissions/categories',
    // Access Control
    setupUserOrg: '/api/access/setup/user-org',
    createUser: '/api/access/users',
    getUserAccessProfile: '/api/access/user/me',
    getUserPermissions: '/api/access/user', // + /{user_id}/permissions
    getUserRoles: '/api/access/users', // + /{user_id}/roles
    assignRole: '/api/access/users', // + /{user_id}/roles
    assignRoleByCode: '/api/access/users', // + /{user_id}/roles/by-code
    removeRole: '/api/access/users', // + /{user_id}/roles
    bulkAssignRole: '/api/access/roles/bulk-assign',
    checkPermission: '/api/access/check',
    checkPermissionBulk: '/api/access/check/bulk',
    // Users
    getUserByExternalId: '/api/access/users/external', // + /{external_id}
    getUser: '/api/access/users', // + /{user_id}
    // v2.0 - User Context (Key for UI Gating)
    getUserContext: '/api/v1/me/context', // + ?account_id={account_id}
    // v2.0 - Groups/Teams
    listGroups: '/api/groups',
    createGroup: '/api/groups',
    getGroup: '/api/groups', // + /{group_id}
    updateGroup: '/api/groups', // + /{group_id}
    deleteGroup: '/api/groups', // + /{group_id}
    getGroupMembers: '/api/groups', // + /{group_id}/members
    addGroupMember: '/api/groups', // + /{group_id}/members
    removeGroupMember: '/api/groups', // + /{group_id}/members/{user_id}
    getGroupRoles: '/api/groups', // + /{group_id}/roles
    assignGroupRole: '/api/groups', // + /{group_id}/roles
    removeGroupRole: '/api/groups', // + /{group_id}/roles/{role_id}
    // v2.0 - Object ACL
    listACL: '/api/acl',
    createACL: '/api/acl',
    updateACL: '/api/acl', // + /{acl_id}
    deleteACL: '/api/acl', // + /{acl_id}
    checkObjectAccess: '/api/acl/check',
    // v2.0 - Approvals
    getApprovalPolicy: '/api/approvals/policy', // + /{account_id}
    createApprovalPolicy: '/api/approvals/policy',
    updateApprovalPolicy: '/api/approvals/policy', // + /{account_id}
    listApprovalRequests: '/api/approvals/requests',
    createApprovalRequest: '/api/approvals/requests',
    getApprovalRequest: '/api/approvals/requests', // + /{request_id}
    submitApprovalDecision: '/api/approvals/requests', // + /{request_id}/decide
    cancelApprovalRequest: '/api/approvals/requests', // + /{request_id}/cancel
    // v2.0 - Audit Logs
    queryAuditLogs: '/api/audit',
    getAuditLogEntry: '/api/audit', // + /{entry_id}
    exportAuditLogs: '/api/audit/export',
  },

  // Payment API Endpoints
  paymentEndpoints: {
    // Payment Methods - Cards
    getPaymentMethods: '/api/payment/methods',
    getCards: '/api/payment/cards',
    addCard: '/api/payment/card/add',
    deleteCard: '/api/payment/card/delete',
    setDefaultCard: '/api/payment/card/default',
    // Payment Methods - ACH
    getACH: '/api/payment/ach',
    addACH: '/api/payment/ach/add',
    deleteACH: '/api/payment/ach/delete',
    setDefaultACH: '/api/payment/ach/default',
    verifyACH: '/api/payment/ach/verify',
    achVerificationStatus: '/api/payment/ach/verification-status',
    processACHPayment: '/api/payment/ach/process-payment',
    // Wallet
    activateWallet: '/api/wallet/activate',
    deactivateWallet: '/api/wallet/deactivate',
    getWallet: '/api/wallet',
    getWalletBalance: '/api/wallet/balance',
    rechargeWallet: '/api/wallet/recharge',
    updateAutoRecharge: '/api/wallet/autorecharge',
    processAutoRecharge: '/api/wallet/process-autorecharge',
    debitWallet: '/api/wallet/debit',
    creditWallet: '/api/wallet/credit',
    getTransactions: '/api/wallet/transactions',
    getCategories: '/api/wallet/categories',
    getCategoriesWithAmounts: '/api/wallet/categories/amounts',
    getInsights: '/api/wallet/insights',
    // Subscription Payments
    setupSubscriptionPayment: '/api/subscription-payment/setup',
    processSubscriptionPayment: '/api/subscription-payment/process',
    getPaymentStatus: '/api/subscription-payment/status',
    // Account (sub-tenant) Wallet
    getAccountWallet: '/api/wallet/account',
    rechargeAccountWallet: '/api/wallet/account/recharge',
    updateAccountAutoRecharge: '/api/wallet/account/autorecharge',
    debitAccountWallet: '/api/wallet/account/debit',
    creditAccountWallet: '/api/wallet/account/credit',
    getAccountTransactions: '/api/wallet/account/transactions',
    getAccountWalletDashboard: '/api/wallet/account/dashboard',
    // Account (sub-tenant) Payment Methods
    getAccountPaymentMethods: '/api/payment/account/methods',
    addAccountCard: '/api/payment/account/card/add',
    deleteAccountCard: '/api/payment/account/card/delete',
    setDefaultAccountCard: '/api/payment/account/card/default',
    addAccountACH: '/api/payment/account/ach/add',
    verifyAccountACH: '/api/payment/account/ach/verify',
    deleteAccountACH: '/api/payment/account/ach/delete',
  },

  // Invoice API Endpoints
  invoiceEndpoints: {
    // Basic Invoice
    getInvoiceSummary: '/api/invoice/summary',
    getInvoice: '/api/invoice',
    downloadInvoice: '/api/invoice/download', // + /{txn_id}
    downloadDynamicInvoice: '/api/invoice/download', // + /{user_id}/{category}/{txn_id}/{invoice_id}
    // Subscription Invoice
    getSubscriptionInvoice: '/api/invoice/subscription',
    generateModuleInvoice: '/api/invoice/module',
    calculateProration: '/api/invoice/prorate/calculate',
    downloadSubscriptionInvoice: '/api/invoice/subscription/download', // + /{user_id}/{billing_period}/{invoice_id}
  },

  // Pricing API Endpoints
  pricingEndpoints: {
    // Pricing Tiers
    getTiers: '/api/pricing/tiers',
    getTierDetails: '/api/pricing/tiers', // + /{tier}
    getTierMetrics: '/api/pricing/tiers', // + /{tier}/metrics
    getQuote: '/api/pricing/quote',
    compareTiers: '/api/pricing/compare',
    // Add-on Modules (public)
    getPricingModules: '/api/pricing/modules',
    // BLYN
    calculateBLYN: '/api/pricing/blyn/calculate',
    getBLYNPacks: '/api/pricing/blyn/packs',
    // SignalStack
    calculateSignalStack: '/api/pricing/signalstack/calculate',
    // AI Decisioning
    calculateAIDecisioning: '/api/pricing/ai-decisioning/calculate',
    // Subscription
    createSubscription: '/api/subscription/create',
    getSubscription: '/api/subscription/get',
    upgradeSubscription: '/api/subscription/upgrade',
    upgradePreview: '/api/subscription/upgrade/preview',
    enableModule: '/api/subscription/module/enable',
    disableModule: '/api/subscription/module/disable',
    getModules: '/api/subscription/modules',
    cancelSubscription: '/api/subscription/cancel',
    contactEnterprise: '/api/subscription/contact-enterprise',
    convertTrial: '/api/subscription/trial/convert',
    // Usage Tracking
    trackUsage: '/api/usage/track',
    trackUsageBatch: '/api/usage/track/batch',
    getUsageSummary: '/api/usage/summary',
    getQuota: '/api/usage/quota',
    getUsageAlerts: '/api/usage/alerts',
    getUsageHistory: '/api/usage/history',
    getBillingSummary: '/api/usage/billing/summary',
    deductOverage: '/api/usage/overage/deduct',
    checkOverageBalance: '/api/usage/overage/check-balance',
    // AI Credits
    assignCredits: '/api/credits/assign',
    deductCredits: '/api/credits/deduct',
    getCreditBalance: '/api/credits/balance',
    getCreditLedger: '/api/credits/ledger',
    getCreditsUsageSummary: '/api/credits/usage-summary',
    getRateCard: '/api/credits/rate-card',
    calculateCredits: '/api/credits/calculate',
    // Price Calculator
    getPriceFields: '/api/price/fields',
    calculatePrice: '/api/price/calculate',
    getDBUSAFields: '/api/price/db-usa/fields',
    calculateDBUSA: '/api/price/db-usa/calculate',
    // Account (sub-tenant) Subscriptions
    createAccountSubscription: '/api/subscription/account/create',
    getAccountSubscription: '/api/subscription/account/get',
    getAccountSubscriptionOverview: '/api/subscription/account/overview',
    upgradeAccountSubscription: '/api/subscription/account/upgrade',
    cancelAccountSubscription: '/api/subscription/account/cancel',
  },

  // Next.js API Routes
  nextApiRoutes: {
    mapboxToken: pathWithAppBase('/api/mapbox-token'),
    predictions: '/api/predictions',
    dashboard: '/api/dashboard',
    cdm: '/api/cdm',
    predictionsUploadFromS3: '/api/predictions/upload-from-s3',
  },

  // Third-party services
  services: {
    mapbox: {
      token: process.env.NEXT_PUBLIC_MAPBOX_TOKEN || 'pk.eyJ1IjoiY29ucmFkNDUiLCJhIjoiY2thMjR6a2tvMDcwdDNtbWtzM3d1NTB6dyJ9.2_XWn13i7rhXn8GusQQeQQ',
    },
    google: {
      apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY || '',
      placesApiKey: process.env.NEXT_PUBLIC_PLACES_API || '',
      mapsScriptUrl: 'https://maps.googleapis.com/maps/api/js',
      geocodingUrl: 'https://maps.googleapis.com/maps/api/geocode/json',
    },
    stripe: {
      publishableKey: process.env.NEXT_PUBLIC_STRIPE_KEY || '',
    },
    predis: {
      appId: process.env.NEXT_PREDIS_APP_ID || '',
      apiUrl: 'https://api.predis.ai/api/v1',
    },
    posthog: {
      key: process.env.NEXT_PUBLIC_POSTHOG_KEY || '',
      host: process.env.NEXT_PUBLIC_POSTHOG_HOST || '',
    },
    stripo: {
      pluginId: process.env.NEXT_PUBLIC_STRIPO_PLUGIN_ID || '',
      secretKey: process.env.NEXT_PUBLIC_STRIPO_SECRET_KEY || '',
    },
    wordpress: {
      assetsBaseUrl: process.env.NEXT_PUBLIC_WORDPRESS_URL || '',
      paths: {
        uploads: '/wp-content/uploads',
        themes: '/wp-content/themes',
        plugins: '/wp-content/plugins',
      },
    },
    pdfjs: {
      cdnUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174',
      workerUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
    },
  },
} as const;

// Freeze the config to make it immutable
Object.freeze(apiConfig);
Object.freeze(apiConfig.baseUrls);
Object.freeze(apiConfig.endpoints);
Object.freeze(apiConfig.integrationEndpoints);
Object.freeze(apiConfig.analyticsEndpoints);
Object.freeze(apiConfig.userLoginEndpoints);
Object.freeze(apiConfig.rbacEndpoints);
Object.freeze(apiConfig.paymentEndpoints);
Object.freeze(apiConfig.invoiceEndpoints);
Object.freeze(apiConfig.pricingEndpoints);
Object.freeze(apiConfig.voiceEndpoints);
Object.freeze(apiConfig.nextApiRoutes);
Object.freeze(apiConfig.services);
Object.freeze(apiConfig.services.mapbox);
Object.freeze(apiConfig.services.google);
Object.freeze(apiConfig.services.stripe);
Object.freeze(apiConfig.services.predis);
Object.freeze(apiConfig.services.posthog);
Object.freeze(apiConfig.services.stripo);
Object.freeze(apiConfig.services.wordpress);
Object.freeze(apiConfig.services.wordpress.paths);
Object.freeze(apiConfig.services.pdfjs);

/** Agentic AI API origin (conversations, process, persona_name, etc.). Never rely on bare NEXT_PUBLIC_AGENTIC_URL — use this so missing env does not produce `undefined` hosts. */
export function getAgenticApiOrigin(): string {
  return apiConfig.baseUrls.agentic;
}

/**
 * Mailhouse Management service (mailhouse-svc) origin — Boostt-internal-staff-only
 * admin tooling (XMPie config, partner production endpoints, template versions).
 *
 * The fallback is a TEMPORARY raw Lambda Function URL, not a real subdomain yet
 * (unlike every other service in `baseUrls`, which route through `*.staging-api.boostt.ai`).
 * Replace this fallback with `https://mailhouse.staging-api.boostt.ai` (or the
 * equivalent prod subdomain) once mailhouse-svc gets a real domain — search for this
 * function's name when that happens, nothing else references the raw URL directly.
 */
const MAILHOUSE_LAMBDA_FALLBACK =
  'https://lnvdcob6pfnh5gyb3cymw3l4su0mtvkk.lambda-url.us-east-2.on.aws';

export function getMailhouseApiOrigin(): string {
  const v = process.env.NEXT_PUBLIC_MAILHOUSE_API_URL;
  return normalizeApiOrigin((v && v.trim()) || MAILHOUSE_LAMBDA_FALLBACK);
}

export function getCommandCenterApiOrigin(): string {
  const v = process.env.NEXT_PUBLIC_COMMAND_CENTER_API_URL;
  return normalizeApiOrigin((v && v.trim()) || apiConfig.baseUrls.commandCenter);
}

